import React, { useCallback, useState } from 'react';

import { Drawer, IconButton, useMediaQuery } from '@mui/material';
import Divider from '@mui/material/Divider';
import { IconMenuDeep } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import AbonoLogoGreen from '@/assets/images//abono-logo-green.svg?react';
import { ENV } from '@/constants/env';
import { MEDIA_QUERY } from '@/constants/media-query';
import { theme } from '@/theme';

import {
    StyledCloseArea,
    StyledContentArea,
    StyledDrawerContent,
    StyledDrawerWrapper,
    StyledHeaderArea,
    StyledLogoArea,
} from './Sidebar.styles';
import { SidebarMenu } from './SidebarMenu';

export interface SidebarProps {
    isSidebarVisible: boolean;
    setSidebarVisible: (isOpened: boolean) => void;
}

export const Sidebar = ({ isSidebarVisible, setSidebarVisible }: SidebarProps) => {
    const { t } = useTranslation();
    const isMobileSize = useMediaQuery(MEDIA_QUERY.ONLY_XS);

    const [isDrawerClosing, setIsDrawerClosing] = useState(false);

    const handleDrawerTransitionEnd = useCallback(() => {
        setIsDrawerClosing(false);
    }, []);

    const handleDrawerToggle = useCallback(() => {
        if (isDrawerClosing) return;

        setIsDrawerClosing(true);
        setSidebarVisible(!isSidebarVisible);
    }, [isDrawerClosing, setSidebarVisible, isSidebarVisible]);

    const handleDrawerClose = useCallback(() => {
        if (!isMobileSize || isDrawerClosing) return;

        setIsDrawerClosing(true);
        setSidebarVisible(false);
    }, [isDrawerClosing, setSidebarVisible, isMobileSize]);

    //

    const sidebarWidth = `${ENV.DEFAULT_SIDEBAR_WIDTH}rem`;

    return (
        <StyledDrawerWrapper width={sidebarWidth} withsidebar={`${isSidebarVisible}`}>
            <Drawer
                open={isSidebarVisible}
                variant={isMobileSize ? 'temporary' : 'persistent'}
                ModalProps={{ keepMounted: true }}
                onClose={handleDrawerClose}
                onTransitionEnd={handleDrawerTransitionEnd}
                sx={{
                    '& .MuiDrawer-paper': {
                        backgroundColor: theme.palette.components.sideBarBackground,
                    },
                }}
            >
                <StyledDrawerContent width={sidebarWidth}>
                    <StyledHeaderArea>
                        <StyledCloseArea>
                            <IconButton onClick={handleDrawerToggle} size="large" aria-label={t('closeSidebar')}>
                                <IconMenuDeep
                                    size={22}
                                    color={theme.palette.components.icon}
                                    style={{ transform: 'scaleX(-1) translateX(.333rem)' }}
                                />
                            </IconButton>
                        </StyledCloseArea>

                        <StyledLogoArea>
                            <AbonoLogoGreen height="52px" />
                        </StyledLogoArea>
                    </StyledHeaderArea>

                    <div>
                        <Divider sx={{ bgcolor: 'components.divider' }} />
                    </div>

                    <StyledContentArea onClick={handleDrawerClose}>
                        <SidebarMenu />
                    </StyledContentArea>
                </StyledDrawerContent>
            </Drawer>
        </StyledDrawerWrapper>
    );
};
